import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { CoachTopBar, useCoachRefresh } from './CoachShell';
import { shareLink, vnShortDate } from '../lib/coach';
import { clock, slotText, absenceLabel, localDate } from '../lib/schedule';
import { today } from '../lib/dates';
import { useApp, useAppPath } from '../lib/AppContext';

const CARD = 'flex flex-col gap-3 rounded-[20px] bg-base-100 p-4';
const GHOST = 'btn h-11 rounded-2xl border-line bg-base-100 font-semibold';
const MAIN = 'btn btn-primary h-11 rounded-2xl border-0 font-bold';

// "15 phút trước", "2 giờ trước", "3 ngày trước".
function ago(iso) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(iso)) / 60000));
  if (minutes < 60) return `${minutes} phút trước`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} giờ trước`;
  return `${Math.round(minutes / 1440)} ngày trước`;
}

function Section({ title, count, note, children }) {
  if (!count) return null;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="flex items-baseline justify-between px-1 pt-2 text-xs font-bold uppercase tracking-wide text-muted">
        <span>{title} · {count}</span>
        <span className="text-[11px] font-medium normal-case tracking-normal">{note}</span>
      </h2>
      {children}
    </section>
  );
}

function Who({ name, url, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar name={name} url={url} size={40} />
      <div className="min-w-0">
        <p className="truncate font-bold">{title}</p>
        {sub && <p className="text-xs text-muted">{sub}</p>}
      </div>
    </div>
  );
}

// "Buổi 18:00 hôm nay" or "Buổi T5 6/11 · 06:30".
const sessionText = (iso) => (localDate(iso) === today() ? `Buổi ${clock(iso)} hôm nay` : `Buổi ${slotText(iso)}`);

// A5 "Yêu cầu": what students sent the coach, in the order the coach deals with it — absences (to know),
// requests to move a session, requests to connect by code, then the invites the coach sent, and the code.
export default function RequestsTab() {
  const { api, coach, native } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const refreshCoach = useCoachRefresh();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => api.get('/coach/requests').then(setData).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); }, [load]);
  const say = (text) => { setToast(text); setTimeout(() => setToast(null), 3500); };

  const run = async (key, fn, message) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (message) say(message);
      await load();
      refreshCoach();
    } catch (e) {
      setError(e.message);
      load();
    } finally {
      setBusy(null);
    }
  };

  const decline = (p) => {
    const note = window.prompt(`Lời nhắn cho ${p.name} (không bắt buộc)`, '');
    if (note === null) return;
    run(`d${p.id}`, () => api.post(`/coach/proposals/${p.id}/decline`, { note }), `Đã giữ giờ cũ cho ${p.name}`);
  };
  const resend = (invite) => run(`r${invite.invite_id}`, async () => {
    const fresh = await api.post(`/coach/invites/${invite.invite_id}/resend`);
    say((await shareLink({ title: 'Lời mời Macro Mate', url: fresh.url })) === 'copied' ? 'Đã sao chép link mời mới' : 'Đã gửi lại lời mời');
  });
  const codeUrl = `${native?.siteUrl || window.location.origin}/c/${coach?.code}`;
  const empty = data && !data.absences.length && !data.proposals.length && !data.requests.length && !data.invites.length;

  return (
    <div className="flex flex-col gap-3">
      <CoachTopBar>
        <button type="button" className="btn h-11 rounded-full border-0 bg-primary px-4 font-bold text-primary-content"
          onClick={() => navigate(path('/coach?invite=1'))}>
          <Icon name="plus" className="size-5" /> Mời
        </button>
      </CoachTopBar>
      <h1 className="text-[26px] font-bold">Yêu cầu</h1>
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="flex flex-col gap-3" aria-busy="true">{[0, 1].map((i) => <div key={i} className="skeleton h-28 rounded-[20px]" />)}</div>}
      {empty && <p className="rounded-[20px] bg-base-100 p-5 text-sm text-muted">Chưa có gì cần bạn xử lý. Học viên báo nghỉ, xin đổi giờ hay gửi yêu cầu kết nối sẽ hiện ở đây.</p>}

      {data && (
        <>
          <Section title="Học viên báo nghỉ" count={data.absences.length} note="chỉ để bạn biết">
            {data.absences.map(({ name, appointment: a }) => (
              <div key={a.id} className={`${CARD} ${a.absence.seen ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <Who name={name} url={a.client_avatar_url} title={`${name} nghỉ ${localDate(a.starts_at) === today() ? 'hôm nay' : slotText(a.starts_at).split(' · ')[0]}`}
                    sub={[sessionText(a.starts_at), a.absence.reason && `“${absenceLabel(a.absence.reason)}”`, ago(a.absence.reported_at)].filter(Boolean).join(' · ')} />
                  {a.absence.seen ? <span className="shrink-0 text-xs font-semibold text-muted">Đã xem</span> : (
                    <button type="button" className="btn btn-sm h-9 shrink-0 rounded-full border-line bg-base-100" disabled={!!busy}
                      onClick={() => run(`s${a.id}`, () => api.post(`/coach/appointments/${a.id}/seen`))}>Đã xem</button>
                  )}
                </div>
              </div>
            ))}
          </Section>

          <Section title="Đề xuất đổi giờ tập" count={data.proposals.length} note="học viên gửi">
            {data.proposals.map((p) => (
              <div key={p.id} className={CARD}>
                <Who name={p.name} url={p.appointment.client_avatar_url} title={`${p.name} · buổi ${slotText(p.previous_starts_at).split(' · ')[0]}`}
                  sub={<>{localDate(p.starts_at) === localDate(p.previous_starts_at) ? '' : `${slotText(p.starts_at).split(' · ')[0]} · `}
                    <s>{clock(p.previous_starts_at)}</s> → <b className="text-base-content">{clock(p.starts_at)}</b></>} />
                {p.note && <p className="rounded-[12px] bg-base-200 px-3 py-2 text-sm italic">“{p.note}”</p>}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" className={GHOST} disabled={!!busy} onClick={() => decline(p)}>Từ chối</button>
                  <button type="button" className={MAIN} disabled={!!busy}
                    onClick={() => run(`a${p.id}`, () => api.post(`/coach/proposals/${p.id}/approve`), `Đã dời buổi của ${p.name} sang ${clock(p.starts_at)}`)}>
                    {busy === `a${p.id}` && <span className="loading loading-spinner loading-sm" />} Duyệt {clock(p.starts_at)}
                  </button>
                </div>
              </div>
            ))}
          </Section>

          <Section title="Yêu cầu kết nối" count={data.requests.length} note="học viên gửi qua mã HLV">
            {data.requests.map((r) => (
              <div key={r.link_id} className={CARD}>
                <Who name={r.name} url={r.avatar_url} title={r.name} sub={`${r.plan_label} · gửi ${ago(r.requested_at)} · qua mã ${data.code}`} />
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" className={GHOST} disabled={!!busy}
                    onClick={() => run(`x${r.link_id}`, () => api.post(`/coach/requests/${r.link_id}/decline`))}>Từ chối</button>
                  <button type="button" className={MAIN} disabled={!!busy}
                    onClick={() => run(`c${r.link_id}`, () => api.post(`/coach/requests/${r.link_id}/accept`), `Đã kết nối với ${r.name}`)}>Chấp nhận</button>
                </div>
              </div>
            ))}
          </Section>

          <Section title="Bạn đã gửi" count={data.invites.length} note="lời mời tặng Pro">
            {data.invites.map((i) => {
              const expired = i.status === 'invite_expired';
              const days = Math.max(1, Math.ceil((new Date(i.expires_at) - Date.now()) / 86400000));
              const how = i.kind === 'qr' ? 'QR tại phòng tập' : 'qua link';
              return (
                <div key={i.invite_id} className={CARD}>
                  <Who name={i.name} title={i.name}
                    sub={expired ? `Hết hạn ${vnShortDate(i.expires_at)} · ${how} · đã trả chỗ` : `Chờ nhận · ${how} · hết hạn sau ${days} ngày · giữ 1 chỗ`} />
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" className={`${GHOST} text-error`} disabled={!!busy}
                      onClick={() => (expired || window.confirm(`Thu hồi lời mời cho ${i.name}? Link sẽ không dùng được nữa.`))
                        && run(`v${i.invite_id}`, () => api.post(`/coach/invites/${i.invite_id}/revoke`), expired ? null : 'Đã thu hồi, chỗ được trả lại')}>
                      {expired ? 'Xoá' : 'Thu hồi'}
                    </button>
                    <button type="button" className={GHOST} disabled={!!busy} onClick={() => resend(i)}>{expired ? 'Mời lại' : 'Gửi lại'}</button>
                  </div>
                </div>
              );
            })}
          </Section>

          <div className="mt-2 flex items-center justify-between gap-3 rounded-[20px] bg-[#15261f] p-4 text-white">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#a9b8b0]">Mã HLV của bạn</p>
              <p className="font-mono text-xl font-extrabold tracking-widest">{data.code}</p>
            </div>
            <button type="button" className="btn h-10 rounded-full border-0 bg-white px-4 font-bold text-[#15261f]"
              onClick={async () => (await shareLink({ title: 'Mã HLV Macro Mate', text: `Kết nối với mình trên Macro Mate, mã HLV: ${data.code}`, url: codeUrl })) === 'copied' && say('Đã sao chép link')}>
              Chia sẻ
            </button>
          </div>
        </>
      )}
      {toast && <div role="status" className="fixed inset-x-4 bottom-28 z-40 mx-auto max-w-sm rounded-2xl bg-[#17201b] px-4 py-3 text-center text-sm font-semibold text-white">{toast}</div>}
    </div>
  );
}
